 
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import {
  Search, Download, X, Pause, Play, Gift, Loader2, ChevronLeft, ChevronRight,
  ShieldAlert, ShieldCheck, KeyRound, MailCheck, Crown, Trash2, Eye, AlertTriangle,
  Ban, RotateCcw, MessageCircle,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { apiClient } from '../../api/client';

const SEGMENTS = [
  { id: '', label: 'All Users' },
  { id: 'b2c_adult', label: 'B2C Adults' },
  { id: 'b2c_teen', label: 'B2C Teens' },
  { id: 'b2b_school', label: 'B2B Schools' },
  { id: 'b2b_corporate', label: 'B2B Corporate' },
  { id: 'paused', label: 'Paused' },
  // Oct 2026 — founder-circle accounts (Robert + family) get their own tab
  // and are excluded from every other view/total so investor screenshots
  // stay clean. Backend list: backend/app/utils/test_accounts.py INSIDER_EMAILS.
  { id: 'founder', label: 'Founder' },
  // Feb 2026 — Robert asked for seed/test accounts to live on their own
  // page so the main Users view shows only real qualified leads. Backend
  // classifier: backend/app/utils/test_accounts.py.
  { id: 'test_accounts', label: 'Test Accounts' },
];
const COLORS = ['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C'];

// Map the canonical subscription_tier code → friendly label shown in the
// "Plan" column. Backend now writes canonical names (e.g. "THE ELITE (Gifted)",
// "MASTER (Gifted)") via app/constants/plans.py — see
// get_gifted_display_name() and the lifespan backfill that migrates any
// legacy "HiBFF Plus (Gifted)" strings. Fall back to "ENTRY" — NOT "Starter"
// — when nothing is set, since a missing tier means the account is on the
// free plan.
// Canonical plan display names. MUST mirror the backend
// `get_subscription_tier_name()` in auth.py / users.py AND the
// `name` field of HOMEPAGE_PLANS in PricingPage.js (single source of truth).
const TIER_LABELS = {
  free: 'FIRST STEPS',
  starter: 'STEADY',
  plus_monthly: 'TOGETHER',
  plus_annual: 'BRAVE',
};

const planLabel = (user) => {
  // If gifted, derive the label from the canonical tier id so monthly⇄annual
  // changes are always visible in the Users table. Backend now writes
  // "THE ELITE (Gifted)" / "MASTER (Gifted)" but legacy rows may still hold
  // the old "HiBFF Plus (Gifted)" string until the lifespan backfill runs.
  if (user?.is_gifted_subscription || user?.is_gifted) {
    const tier = user?.subscription_tier;
    if (tier && TIER_LABELS[tier]) return `${TIER_LABELS[tier]} (Gifted)`;
    // Fallback: trust whatever the backend gave us (handles legacy tiers).
    return user?.subscription_tier_name || 'FIRST STEPS (Gifted)';
  }
  if (user?.subscription_tier_name) return user.subscription_tier_name;
  const tier = user?.subscription_tier;
  if (tier && TIER_LABELS[tier]) return TIER_LABELS[tier];
  if (tier) return tier;
  return 'FIRST STEPS';
};

const fmtLocal = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  } catch { return ts; }
};

const AdminUsers = () => {
  const [searchParams] = useSearchParams();
  const [segment, setSegment] = useState(searchParams.get('segment') || '');
  // ?search=<query> — set by the Activity Pulse drill-down (2026-07-03)
  // so clicking "Open in Users → usr_abc123" from a heatmap cell lands
  // here with the target already filtered.
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showQuarantined, setShowQuarantined] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userJourney, setUserJourney] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [giftHearts, setGiftHearts] = useState('');
  const [exactHearts, setExactHearts] = useState('');
  const [locationVal, setLocationVal] = useState('');
  const [geoBlockedCountries, setGeoBlockedCountries] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmQuarantine, setConfirmQuarantine] = useState(false);
  const [disposableUsers, setDisposableUsers] = useState(null);
  // Blocked emails — admin can list and unblock previously-deleted users
  // so they can rejoin via Google OAuth or password signup.
  const [showBlocked, setShowBlocked] = useState(false);
  const [purging, setPurging] = useState(false);
  const [verifQueue, setVerifQueue] = useState([]);
  const [blockedEmails, setBlockedEmails] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);

  const loadBlocked = useCallback(async () => {
    setBlockedLoading(true);
    try {
      const data = await apiClient.request('/api/admin/blocked-emails');
      setBlockedEmails(data?.blocked || []);
    } catch (e) {
      setBlockedEmails([]);
    } finally {
      setBlockedLoading(false);
    }
  }, []);

  const handleUnblock = async (email) => {
    if (!window.confirm(`Unblock ${email}? They'll be able to sign up again on their next login attempt.`)) return;
    try {
      await apiClient.request(`/api/admin/blocked-emails/${encodeURIComponent(email)}`, { method: 'DELETE' });
      await loadBlocked();
    } catch (e) {
      window.alert('Unblock failed: ' + (e?.message || 'unknown error'));
    }
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (segment) params.append('segment', segment);
    if (search) params.append('search', search);
    if (showQuarantined) params.append('show_quarantined', 'true');
    if (showDeleted) params.append('show_deleted', 'true');
    let list = [];
    try {
      const data = await apiClient.request(`/api/admin/users?${params}`);
      list = data.users || [];
      setUsers(list);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
    return list;
  }, [page, search, segment, showQuarantined, showDeleted]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    apiClient.request('/api/admin/security/geo-block')
      .then(r => setGeoBlockedCountries(r.enabled ? (r.countries || []) : []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadVerifQueue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Maya Helps — users with an open help message get a waiting icon
  const [helpWaitIds, setHelpWaitIds] = useState([]);
  useEffect(() => {
    apiClient.request('/api/help/admin/open-user-ids')
      .then(d => setHelpWaitIds(d.user_ids || []))
      .catch(() => {});
  }, []);

  const loadUserDetail = async (userId) => {
    try {
      const [data, journey] = await Promise.all([
        apiClient.request(`/api/admin/user/${userId}`),
        apiClient.request(`/api/admin/user/${userId}/journey`).catch(() => ({ events: [] })),
      ]);
      setUserDetail(data);
      setUserJourney(journey);
      setSelectedUser(userId);
    } catch (e) { console.error(e); }
  };

  const executeAction = async (userId, action, value) => {
    setActionLoading(action);
    setActionResult(null);
    try {
      const res = await apiClient.request(`/api/admin/user/${userId}/action`, {
        method: 'POST', body: JSON.stringify({ action, value: value !== undefined ? String(value) : undefined }),
      });
      setActionResult({ ok: true, message: res.message || 'Done.' });
      setGiftHearts('');
      setConfirmDelete(false);
      setConfirmQuarantine(false);
      if (selectedUser) loadUserDetail(selectedUser);
      // Refresh users list AND swap the modal's stale user object for the
      // fresh row so the Gift Subscription "currently on" indicator and
      // active-button state reflect the action immediately.
      const fresh = await loadUsers();
      if (Array.isArray(fresh)) {
        const updated = fresh.find(u => u.user_id === userId);
        if (updated) {
          setActionModal(prev => prev ? { ...prev, user: updated } : prev);
        }
      }
      if (action === 'delete' || action === 'hard_delete') {
        setTimeout(() => { setActionModal(null); setActionResult(null); }, 1500);
      }
    } catch (e) {
      setActionResult({ ok: false, message: e?.message || 'Action failed.' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    const params = new URLSearchParams({ format: 'csv' });
    const data = await apiClient.request(`/api/admin/export/users?${params}`);
    const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'hibff_users.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const loadDisposable = async () => {
    try {
      const data = await apiClient.request('/api/admin/users/disposable');
      setDisposableUsers(data);
    } catch (e) { console.error(e); }
  };

  const loadVerifQueue = async () => {
    try {
      const d = await apiClient.request('/api/verification/admin/queue');
      setVerifQueue(d.queue || []);
    } catch (e) { /* silent */ }
  };

  const decideVerification = async (userId, approve) => {
    if (!window.confirm(`${approve ? 'APPROVE' : 'REJECT'} this parent's identity verification?`)) return;
    try {
      await apiClient.request(`/api/verification/admin/${userId}/decision`, {
        method: 'POST', body: JSON.stringify({ approve }),
      });
      loadVerifQueue();
    } catch (e) { window.alert(`Failed: ${e.message}`); }
  };

  const purgeTestData = async () => {
    setPurging(true);
    try {
      // Dry run first — show exactly what would be deleted before confirming.
      const preview = await apiClient.request('/api/admin/purge-test-data?dry_run=true', { method: 'POST' });
      const t = preview?.targets || {};
      const userCount = t.users_qa?.would_delete ?? 0;
      const leadCount = (t.welcome_pack_leads?.would_delete ?? 0) + (t.waitlist?.would_delete ?? 0);
      if (userCount + leadCount === 0) {
        window.alert('Nothing to purge — no test accounts or test leads found.');
        return;
      }
      const sample = (t.users_qa?.emails || []).slice(0, 8).join('\n');
      if (!window.confirm(`Permanently delete ${userCount} test accounts (+ ${leadCount} test leads/waitlist rows) and all their data?\n\nSample:\n${sample}\n\nAdmin accounts are never touched. This cannot be undone.`)) return;
      const result = await apiClient.request('/api/admin/purge-test-data?dry_run=false', { method: 'POST' });
      const deleted = result?.targets?.users_qa?.deleted?.users ?? 0;
      window.alert(`Purge complete — ${deleted} test accounts deleted. ${result?.counts_after?.users ?? '?'} users remain.`);
      loadUsers();
    } catch (e) {
      window.alert(`Purge failed: ${e.message}`);
    } finally {
      setPurging(false);
    }
  };

  const bulkQuarantineDisposable = async () => {
    if (!window.confirm(`Quarantine all ${disposableUsers?.count || 0} disposable-email accounts? This is reversible.`)) return;
    try {
      const r = await apiClient.request('/api/admin/users/cleanup-disposable', { method: 'POST' });
      alert(`Quarantined ${r.quarantined} user(s).`);
      setDisposableUsers(null);
      loadUsers();
    } catch (e) {
      alert(`Failed: ${e?.message || 'unknown error'}`);
    }
  };

  return (
    <AdminLayout pageTitle="Users">
      <CheatSheet pageId="users" />
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          {/* Tabs */}
          {helpWaitIds.length > 0 && (
            <a href="/admin/help-requests" data-testid="help-waiting-banner"
              className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-xl text-sm font-semibold border"
              style={{ backgroundColor: '#FFF5EA', borderColor: '#FDBA74', color: '#C2410C' }}>
              <MessageCircle size={16} />
              {helpWaitIds.length} Maya Helps message{helpWaitIds.length > 1 ? 's' : ''} waiting — click to reply
            </a>
          )}
          {verifQueue.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="verification-review-panel">
              <p className="font-semibold text-sm text-amber-900 mb-2">🛂 Parent Identity Verification — {verifQueue.length} awaiting your review</p>
              {verifQueue.map((v) => (
                <div key={v.user_id} className="flex items-center gap-3 py-2 border-t border-amber-200 text-sm" data-testid={`verif-row-${v.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{v.name}</span> <span className="text-gray-500 text-xs">{v.email}</span>
                    <p className="text-xs text-amber-800">{v.id_verification?.reason}</p>
                  </div>
                  <button onClick={async () => {
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verification/admin/${v.user_id}/photo`, { headers: { Authorization: `Bearer ${localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token') || ''}` } });
                      const b = await r.blob();
                      window.open(URL.createObjectURL(b), '_blank');
                    } catch (e) { window.alert('Photo load failed'); }
                  }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-amber-300 text-amber-900" data-testid={`verif-photo-${v.user_id}`}>View photo</button>
                  <button onClick={() => decideVerification(v.user_id, true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#16A34A' }} data-testid={`verif-approve-${v.user_id}`}>Approve</button>
                  <button onClick={() => decideVerification(v.user_id, false)} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#DC2626' }} data-testid={`verif-reject-${v.user_id}`}>Reject</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 overflow-x-auto" data-testid="users-segment-tabs">
            {SEGMENTS.map(s => (
              <button key={s.id} data-testid={`users-tab-${s.id || 'all'}`}
                onClick={() => { setSegment(s.id); setPage(1); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${segment === s.id ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                style={segment === s.id ? { color: '#1A1209' } : {}}>{s.label}</button>
            ))}
          </div>

          {/* Search + Filters */}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="users-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search name, email, or ID..." className="pl-9 pr-4 py-2 border rounded-lg text-sm w-80 max-w-full" style={{ borderColor: '#E0E0E0' }} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs" style={{ color: '#44403A' }}>
                <input data-testid="users-show-quarantined-toggle" type="checkbox" checked={showQuarantined}
                  onChange={e => { setShowQuarantined(e.target.checked); setPage(1); }} className="rounded" />
                Show quarantined
              </label>
              <label className="flex items-center gap-1.5 text-xs" style={{ color: '#44403A' }}>
                <input data-testid="users-show-deleted-toggle" type="checkbox" checked={showDeleted}
                  onChange={e => { setShowDeleted(e.target.checked); setPage(1); }} className="rounded" />
                Show deleted
              </label>
              <button data-testid="users-disposable-review-btn" onClick={loadDisposable}
                className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                <AlertTriangle size={12} /> Review disposable
              </button>
              <button data-testid="users-blocked-emails-btn"
                onClick={() => { setShowBlocked((v) => { if (!v) loadBlocked(); return !v; }); }}
                className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                <Ban size={12} /> Blocked emails
              </button>
              {segment === 'test_accounts' && (
                <button data-testid="users-purge-test-data-btn" onClick={purgeTestData} disabled={purging}
                  className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ backgroundColor: '#7F1D1D', color: '#FFFFFF' }}>
                  {purging ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Purge Test Data
                </button>
              )}
              <button data-testid="users-export-csv-btn" onClick={handleExport}
                className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 text-white" style={{ backgroundColor: '#F5831F' }}>
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {/* Blocked emails panel — collapsible, shows admin-blocked
              emails with one-click unblock so deleted users can rejoin
              when admin approves. */}
          {showBlocked && (
            <div className="mb-4 bg-white rounded-lg p-4" style={{ border: '1px solid #FECACA' }} data-testid="blocked-emails-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Ban size={14} style={{ color: '#991B1B' }} />
                  <h3 className="text-sm font-semibold" style={{ color: '#1A1209' }}>
                    Blocked emails ({blockedEmails.length})
                  </h3>
                </div>
                <button onClick={() => setShowBlocked(false)} className="text-xs underline" style={{ color: '#666' }}>
                  Close
                </button>
              </div>
              <p className="text-xs mb-3" style={{ color: '#666' }}>
                Emails on this list cannot sign up via Google OAuth or password registration.
                Unblock any time to let them rejoin — the next sign-in creates a fresh account.
              </p>
              {blockedLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : blockedEmails.length === 0 ? (
                <p className="text-xs italic text-gray-500">No blocked emails.</p>
              ) : (
                <table className="w-full text-xs" data-testid="blocked-emails-table">
                  <thead>
                    <tr style={{ backgroundColor: '#FFF7F7' }}>
                      <th className="text-left py-1.5 px-2 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-gray-700">Blocked at</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-gray-700">By</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-gray-700">Reason</th>
                      <th className="text-right py-1.5 px-2 font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedEmails.map((b) => (
                      <tr key={b.email} className="border-t border-gray-100">
                        <td className="py-1.5 px-2">{b.email}</td>
                        <td className="py-1.5 px-2 text-gray-500">{(b.blocked_at || '').slice(0, 10)}</td>
                        <td className="py-1.5 px-2 text-gray-500">{b.blocked_by || '—'}</td>
                        <td className="py-1.5 px-2 text-gray-500">{b.reason || '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          <button
                            onClick={() => handleUnblock(b.email)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium text-white"
                            style={{ backgroundColor: '#2EAA5C' }}
                            data-testid={`unblock-${b.email}`}
                          >
                            <RotateCcw size={10} /> Unblock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg overflow-x-auto" style={{ border: '1px solid #E0E0E0' }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                {['Name', 'Email', 'Segment', 'Location', 'Source', 'Plan', 'Signed Up', 'Streak', 'Hearts', 'Actions'].map(h => (
                  <th key={h} className="text-left p-3 font-medium text-xs" style={{ color: '#44403A' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody data-testid="users-table-body">
                {loading ? (
                  <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-400 text-sm">No users found</td></tr>
                ) : users.map(user => {
                  const isQuarantined = user.account_status === 'quarantined';
                  const isSuspended = user.account_status === 'suspended';
                  const isDeleted = user.account_status === 'deleted';
                  const unconfirmed = user.email_confirmed === false;
                  return (
                    <tr key={user.user_id} data-testid={`user-row-${user.user_id}`} className="border-t hover:bg-gray-50 cursor-pointer" style={{ borderColor: '#E0E0E0', backgroundColor: isQuarantined ? '#FEF2F2' : isSuspended ? '#FFFBEB' : 'inherit' }} onClick={() => loadUserDetail(user.user_id)}>
                      <td className="p-3 font-medium" style={{ color: '#333333' }}>
                        <div className="flex items-center gap-1.5">
                          {user.name || '—'}
                          {helpWaitIds.includes(user.user_id) && (
                            <span title="Maya Helps message waiting" data-testid={`help-waiting-${user.user_id}`}>
                              <MessageCircle size={13} style={{ color: '#EA580C' }} fill="#FED7AA" />
                            </span>
                          )}
                          {isQuarantined && <span title="Quarantined"><ShieldAlert size={12} style={{ color: '#DC2626' }} /></span>}
                          {isSuspended && <span title="Suspended"><Pause size={12} style={{ color: '#92400E' }} /></span>}
                          {isDeleted && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }} title={`Deleted ${user.deleted_at?.slice(0, 10) || ''}`}>Deleted</span>}
                        </div>
                      </td>
                      <td className="p-3 text-xs" style={{ color: '#44403A' }}>
                        <div className="flex items-center gap-1.5">
                          {user.email}
                          {unconfirmed && <span title="Email not confirmed" className="px-1 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Unconfirmed</span>}
                        </div>
                      </td>
                      <td className="p-3"><span className="px-2 py-0.5 text-xs rounded-full font-medium" style={{ backgroundColor: user.subscription_status === 'paused' ? '#FEF3C715' : user.age_band === 'teen' ? '#7B4FD815' : user.role === 'school_admin' ? '#2563EB15' : user.role === 'corporate_admin' || user.role === 'corporate_employee' ? '#10B98115' : '#F5831F15', color: user.subscription_status === 'paused' ? '#92400E' : user.age_band === 'teen' ? '#7B4FD8' : user.role === 'school_admin' ? '#2563EB' : user.role === 'corporate_admin' || user.role === 'corporate_employee' ? '#10B981' : '#F5831F' }}>{user.subscription_status === 'paused' ? 'Paused' : user.age_band === 'teen' ? 'Teen' : user.role === 'school_admin' ? 'School' : user.role === 'corporate_admin' || user.role === 'corporate_employee' ? 'Corporate' : 'Adult'}</span></td>
                      <td className="p-3 text-xs" data-testid={`user-location-${user.user_id}`} style={{ color: '#44403A' }}>
                        {user.geo?.country
                          ? <>
                              {`${user.geo.city ? user.geo.city + ', ' : ''}${user.geo.country_code || user.geo.country}`}
                              {geoBlockedCountries.includes((user.geo.country_code || '').toUpperCase()) &&
                                <span title="Signups from this country are now blocked" className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#DC262612', color: '#DC2626' }} data-testid={`user-geoblocked-${user.user_id}`}>BLOCKED REGION</span>}
                            </>
                          : <span style={{ color: '#9CA3AF' }}>Unknown</span>}
                      </td>
                      <td className="p-3" data-testid={`user-source-${user.user_id}`}>
                        {user.signup_utm_source ? (
                          <span className="px-2 py-0.5 text-xs rounded-full font-medium capitalize" style={{ backgroundColor: user.signup_utm_source === 'reddit' ? '#FF450015' : user.signup_utm_source === 'quora' ? '#B9241015' : '#6366F115', color: user.signup_utm_source === 'reddit' ? '#FF4500' : user.signup_utm_source === 'quora' ? '#B92410' : '#6366F1' }}>{user.signup_utm_source}</span>
                        ) : (
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>Organic</span>
                        )}
                      </td>
                      <td className="p-3 text-xs" data-testid={`user-plan-${user.user_id}`} style={{ color: '#44403A' }}>
                        <span>{planLabel(user)}</span>
                        {user.is_gifted && (
                          <span
                            className="ml-2 px-1.5 py-0.5 text-[10px] rounded font-semibold"
                            style={{ backgroundColor: '#F5831F15', color: '#F5831F', border: '1px solid #F5831F40' }}
                            title="This user is on a complimentary / gifted plan"
                            data-testid={`user-gifted-badge-${user.user_id}`}
                          >
                            Gifted
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs" style={{ color: '#44403A' }}>{user.created_at?.slice(0, 10)}</td>
                      <td className="p-3">{user.current_streak || 0}</td>
                      <td className="p-3">{user.hearts_balance || 0}</td>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        <button data-testid={`user-actions-btn-${user.user_id}`} onClick={() => { setActionModal({ user }); setActionResult(null); }} className="text-xs font-medium" style={{ color: '#F5831F' }}>Actions</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {total > 20 && (
              <div className="p-3 flex items-center justify-center gap-3 border-t" style={{ borderColor: '#E0E0E0' }}>
                <button data-testid="users-pagination-prev" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded disabled:opacity-30"><ChevronLeft size={16} /></button>
                <span className="text-sm" style={{ color: '#44403A' }}>Page {page} of {Math.ceil(total / 20)}</span>
                <button data-testid="users-pagination-next" onClick={() => setPage(p => p + 1)} disabled={users.length < 20} className="p-1.5 rounded disabled:opacity-30"><ChevronRight size={16} /></button>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-full lg:w-72 space-y-4 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h4 className="text-sm font-bold mb-2" style={{ color: '#1A1209' }}>Segment Breakdown</h4>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart><Pie data={[{ name: 'Adults', value: users.filter(u => u.age_band !== 'teen' && u.role !== 'school_admin').length || 1 }, { name: 'Teens', value: users.filter(u => u.age_band === 'teen').length || 0 }, { name: 'Schools', value: users.filter(u => u.role === 'school_admin').length || 0 }]} dataKey="value" cx="50%" cy="50%" outerRadius={50} label>
                {COLORS.slice(0, 3).map((c, i) => <Cell key={i} fill={c} />)}
              </Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h4 className="text-sm font-bold mb-2" style={{ color: '#1A1209' }}>Total: {total} users</h4>
            <p className="text-xs" style={{ color: '#44403A' }}>Showing page {page}</p>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#F5831F' }}>{(userDetail.user?.name || 'U')[0]}</div>
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: '#1A1209' }}>{userDetail.user?.name}</h3>
                    <p className="text-sm" style={{ color: '#44403A' }}>{userDetail.user?.email} · {userDetail.user?.role || 'user'} · {userDetail.user?.age_band || 'adult'}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedUser(null)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="user-detail-close"><X size={20} /></button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Sessions', value: userDetail.stats?.sessions },
                  { label: 'Challenges', value: userDetail.stats?.challenges },
                  { label: 'Badges', value: userDetail.stats?.badges },
                  { label: 'Hearts', value: userDetail.user?.hearts_balance || 0 },
                  { label: 'Plan', value: userDetail.subscription?.plan_name || 'Starter' },
                  { label: 'Joined', value: userDetail.user?.created_at?.slice(0, 10) },
                  { label: 'Location', value: userDetail.user?.geo?.country ? `${userDetail.user.geo.city ? userDetail.user.geo.city + ', ' : ''}${userDetail.user.geo.country}` : 'Unknown', testId: 'user-location' },
                ].map((m, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }}>
                    <p className="text-xs" style={{ color: '#44403A' }}>{m.label}</p>
                    <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{m.value}</p>
                  </div>
                ))}
              </div>

              {userJourney?.events?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>User Journey</h4>
                  <div className="relative pl-6">
                    <div className="absolute left-[9px] top-2 bottom-2 w-[2px]" style={{ backgroundColor: '#E0E0E0' }} />
                    {userJourney.events.map((ev, i) => {
                      const colors = { signup: '#2EAA5C', first_session: '#F5831F', first_challenge: '#7B4FD8', first_badge: '#F59E0B', upgrade: '#2563EB', charter: '#DC2626', milestone: '#F5831F', last_active: '#44403A' };
                      return (
                        <div key={i} className="relative flex items-start gap-3 pb-4">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 -ml-6 z-10" style={{ backgroundColor: colors[ev.type] || '#666', border: '2px solid white' }}>
                            <div className="w-2 h-2 rounded-full bg-white" />
                          </div>
                          <div className="pt-0.5">
                            <p className="text-sm font-medium" style={{ color: '#333333' }}>{ev.label}</p>
                            <p className="text-xs" style={{ color: '#44403A' }}>{ev.detail}</p>
                            <p className="text-xs" style={{ color: '#6B6257' }}>{fmtLocal(ev.timestamp)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <h4 className="text-sm font-bold mb-2" style={{ color: '#1A1209' }}>Activity Timeline</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg p-3" style={{ backgroundColor: '#F9F9F9' }}>
                {(userDetail.recent_audit || []).map((a, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                    <span style={{ color: '#333333' }}>{a.action}</span>
                    <span style={{ color: '#6B6257' }}>{fmtLocal(a.timestamp)}</span>
                  </div>
                ))}
                {(!userDetail.recent_audit || userDetail.recent_audit.length === 0) && <p className="text-xs text-gray-400">No activity recorded</p>}
              </div>

              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t" style={{ borderColor: '#E0E0E0' }}>
                <button data-testid="detail-action-suspend" onClick={() => executeAction(selectedUser, 'suspend')} className="px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 flex items-center gap-1.5"><Pause size={14} /> Suspend</button>
                <button data-testid="detail-action-unsuspend" onClick={() => executeAction(selectedUser, 'unsuspend')} className="px-3 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 flex items-center gap-1.5"><Play size={14} /> Unsuspend</button>
                <button data-testid="detail-action-gift-100" onClick={() => executeAction(selectedUser, 'gift_hearts', '100')} className="px-3 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 flex items-center gap-1.5"><Gift size={14} /> +100 Hearts</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disposable Users Review Modal */}
      {disposableUsers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setDisposableUsers(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="disposable-users-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
                <AlertTriangle size={18} style={{ color: '#92400E' }} /> Disposable-email users ({disposableUsers.count})
              </h3>
              <button onClick={() => setDisposableUsers(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="text-sm mb-4" style={{ color: '#44403A' }}>
              These accounts were created with throwaway / temporary email addresses (mailinator, tempmail, badgerhole, etc.).
              Bulk-quarantine is fully reversible — accounts are hidden from the main list but kept in the database for audit.
            </p>
            {disposableUsers.count === 0 ? (
              <p className="text-center py-8 text-sm text-gray-400" data-testid="disposable-empty">No disposable-email users found.</p>
            ) : (
              <>
                <div className="space-y-1 mb-4 max-h-80 overflow-y-auto" data-testid="disposable-user-list">
                  {disposableUsers.users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-2 rounded text-xs" style={{ backgroundColor: '#FEF2F2' }}>
                      <div>
                        <span className="font-medium" style={{ color: '#1A1209' }}>{u.name || '—'}</span>
                        <span className="ml-2" style={{ color: '#44403A' }}>{u.email}</span>
                      </div>
                      <span className="px-2 py-0.5 rounded font-medium" style={{ backgroundColor: '#DC2626', color: 'white' }}>{u.disposable_domain}</span>
                    </div>
                  ))}
                </div>
                <button data-testid="disposable-bulk-quarantine-btn" onClick={bulkQuarantineDisposable}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#DC2626' }}>
                  <ShieldAlert size={14} /> Quarantine all {disposableUsers.count}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Actions Modal — full rewrite */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => { setActionModal(null); setActionResult(null); setConfirmDelete(false); setConfirmQuarantine(false); }}>
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="user-action-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold" style={{ color: '#1A1209' }}>Actions: {actionModal.user.name || '—'}</h3>
                <p className="text-xs" style={{ color: '#44403A' }}>{actionModal.user.email}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {actionModal.user.account_status === 'quarantined' && <span className="px-1.5 py-0.5 text-[10px] rounded font-medium" style={{ backgroundColor: '#DC2626', color: 'white' }}>QUARANTINED</span>}
                  {actionModal.user.account_status === 'suspended' && <span className="px-1.5 py-0.5 text-[10px] rounded font-medium" style={{ backgroundColor: '#F59E0B', color: 'white' }}>SUSPENDED</span>}
                  {actionModal.user.email_confirmed === false && <span className="px-1.5 py-0.5 text-[10px] rounded font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>UNCONFIRMED</span>}
                </div>
              </div>
              <button onClick={() => setActionModal(null)} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
            </div>

            {actionResult && (
              <div data-testid="action-result-banner" className={`p-2.5 rounded-lg text-xs mb-3 ${actionResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {actionResult.message}
              </div>
            )}

            <div className="space-y-2">
              <button data-testid="action-view-profile" onClick={() => { setActionModal(null); loadUserDetail(actionModal.user.user_id); }}
                className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-100"
                style={{ backgroundColor: '#F9F9F9', color: '#1A1209' }}>
                <Eye size={14} /> View full profile
              </button>

              {actionModal.user.account_status === 'suspended' ? (
                <button data-testid="action-unsuspend" disabled={actionLoading === 'unsuspend'}
                  onClick={() => executeAction(actionModal.user.user_id, 'unsuspend')}
                  className="w-full py-2 px-4 bg-green-50 text-green-700 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {actionLoading === 'unsuspend' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Unsuspend account
                </button>
              ) : (
                <button data-testid="action-suspend" disabled={actionLoading === 'suspend' || actionModal.user.account_status === 'quarantined'}
                  onClick={() => executeAction(actionModal.user.user_id, 'suspend')}
                  className="w-full py-2 px-4 bg-red-50 text-red-700 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {actionLoading === 'suspend' ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />} Suspend account
                </button>
              )}

              {actionModal.user.account_status === 'deleted' && (
                <button data-testid="action-restore" disabled={actionLoading === 'restore'}
                  onClick={() => executeAction(actionModal.user.user_id, 'restore')}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>
                  {actionLoading === 'restore' ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Restore account (undo delete)
                </button>
              )}
              {actionModal.user.account_status === 'quarantined' ? (
                <button data-testid="action-unquarantine" disabled={actionLoading === 'unquarantine'}
                  onClick={() => executeAction(actionModal.user.user_id, 'unquarantine')}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>
                  {actionLoading === 'unquarantine' ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Lift quarantine
                </button>
              ) : confirmQuarantine ? (
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
                  <p className="text-xs mb-2" style={{ color: '#7F1D1D' }}>Quarantine hides the user from the main list and blocks their login. Reversible.</p>
                  <div className="flex gap-1.5">
                    <button data-testid="action-quarantine-confirm" onClick={() => executeAction(actionModal.user.user_id, 'quarantine', 'manual_admin_action')}
                      className="flex-1 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#DC2626' }}>Confirm</button>
                    <button onClick={() => setConfirmQuarantine(false)} className="flex-1 py-1.5 rounded text-xs font-medium" style={{ backgroundColor: '#F9F9F9', color: '#1A1209' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button data-testid="action-quarantine" onClick={() => setConfirmQuarantine(true)}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#FEF2F2', color: '#7F1D1D' }}>
                  <ShieldAlert size={14} /> Quarantine (suspected abuse)
                </button>
              )}

              <button data-testid="action-reset-password" disabled={actionLoading === 'reset_password'}
                onClick={() => executeAction(actionModal.user.user_id, 'reset_password')}
                className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                {actionLoading === 'reset_password' ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Send password reset email
              </button>

              {actionModal.user.email_confirmed === false && (
                <>
                <button data-testid="action-mark-confirmed" disabled={actionLoading === 'mark_confirmed'}
                  onClick={() => executeAction(actionModal.user.user_id, 'mark_confirmed')}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#ECFDF5', color: '#047857' }}>
                  {actionLoading === 'mark_confirmed' ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />} Mark email confirmed (remove badge)
                </button>
                <button data-testid="action-resend-confirmation" disabled={actionLoading === 'resend_confirmation'}
                  onClick={() => executeAction(actionModal.user.user_id, 'resend_confirmation')}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                  {actionLoading === 'resend_confirmation' ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />} Re-send confirmation email
                </button>
                </>
              )}

              <div className="pt-2 border-t" style={{ borderColor: '#E0E0E0' }}>
                <p className="text-[11px] mb-1.5 font-medium" style={{ color: '#44403A' }}>Gift Hearts</p>
                <div className="flex gap-2">
                  <input data-testid="action-gift-hearts-input" type="number" min="1" max="10000" value={giftHearts}
                    onChange={e => setGiftHearts(e.target.value)} placeholder="Amount"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                  <button data-testid="action-gift-hearts-btn" disabled={!giftHearts || actionLoading === 'gift_hearts'}
                    onClick={() => executeAction(actionModal.user.user_id, 'gift_hearts', giftHearts)}
                    className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    {actionLoading === 'gift_hearts' ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />} Gift
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t" style={{ borderColor: '#E0E0E0' }}>
                <p className="text-[11px] mb-1.5 font-medium" style={{ color: '#44403A' }}>
                  Set Hearts (exact balance)
                  <span className="ml-2 text-[10px]" style={{ color: '#9CA3AF' }}>currently {actionModal.user.hearts_balance || 0}</span>
                </p>
                <div className="flex gap-2">
                  <input data-testid="action-set-hearts-input" type="number" min="0" max="100000" value={exactHearts}
                    onChange={e => setExactHearts(e.target.value)} placeholder="New balance"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                  <button data-testid="action-set-hearts-btn" disabled={exactHearts === '' || actionLoading === 'set_hearts'}
                    onClick={() => executeAction(actionModal.user.user_id, 'set_hearts', exactHearts)}
                    className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: '#EDE9FE', color: '#6D28D9' }}>
                    {actionLoading === 'set_hearts' ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />} Set
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t" style={{ borderColor: '#E0E0E0' }}>
                <p className="text-[11px] mb-1.5 font-medium" style={{ color: '#44403A' }}>
                  Set Location
                  <span className="ml-2 text-[10px]" style={{ color: '#9CA3AF' }}>e.g. London, GB — overrides IP lookup permanently</span>
                </p>
                <div className="flex gap-2">
                  <input data-testid="action-set-location-input" type="text" value={locationVal}
                    onChange={e => setLocationVal(e.target.value)} placeholder="City, Country"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                  <button data-testid="action-set-location-btn" disabled={!locationVal.trim() || actionLoading === 'set_location'}
                    onClick={() => executeAction(actionModal.user.user_id, 'set_location', locationVal.trim())}
                    className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>
                    {actionLoading === 'set_location' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Set
                  </button>
                </div>
              </div>

              <div>
                <p className="text-[11px] mb-1.5 font-medium" style={{ color: '#44403A' }}>
                  Gift Subscription
                  {(actionModal.user.is_gifted_subscription || actionModal.user.is_gifted) && actionModal.user.subscription_tier && (
                    <span className="ml-2 text-[10px] font-semibold" style={{ color: '#7B4FD8' }}>
                      · currently on {{ starter: 'Steady', plus_monthly: 'Together', plus_annual: 'Brave' }[actionModal.user.subscription_tier] || actionModal.user.subscription_tier}
                    </span>
                  )}
                </p>
                <div className="flex gap-2">
                  {(() => {
                    const currentTier = actionModal.user.subscription_tier;
                    const isGifted = actionModal.user.is_gifted_subscription || actionModal.user.is_gifted;
                    const GIFT_PLANS = [
                      { id: 'starter', label: 'Steady' },
                      { id: 'plus_monthly', label: 'Together' },
                      { id: 'plus_annual', label: 'Brave' },
                    ];
                    return GIFT_PLANS.map(({ id, label }) => {
                      const active = isGifted && currentTier === id;
                      return (
                        <button key={id} data-testid={`action-gift-sub-${id}`}
                          disabled={actionLoading === 'gift_subscription'}
                          onClick={() => executeAction(actionModal.user.user_id, 'gift_subscription', id)}
                          className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 disabled:opacity-50"
                          style={{
                            backgroundColor: active ? '#7B4FD8' : '#F3E8FF',
                            color: active ? 'white' : '#7B4FD8',
                            border: active ? '2px solid #7B4FD8' : '2px solid transparent',
                          }}>
                          <Crown size={12} /> {label}{active ? ' ✓' : ''}
                        </button>
                      );
                    });
                  })()}
                </div>
                <p className="text-[10px] mt-1" style={{ color: '#999' }}>The teen receives a &quot;HiBFF has chosen YOU&quot; gift email + SMS automatically.</p>
                {(actionModal.user.is_gifted_subscription || actionModal.user.is_gifted) && (
                  <button data-testid="action-remove-gift" disabled={actionLoading === 'remove_gift'}
                    onClick={() => executeAction(actionModal.user.user_id, 'remove_gift')}
                    className="mt-2 w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: '#FEF2F2', color: '#B91C1C' }}>
                    {actionLoading === 'remove_gift' ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />} Remove gifted plan (back to free)
                  </button>
                )}
              </div>

              <div className="pt-2 border-t" style={{ borderColor: '#E0E0E0' }}>
                {confirmDelete ? (
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
                    <p className="text-xs mb-2" style={{ color: '#7F1D1D' }}>Soft delete keeps the record (email stays taken). Hard delete permanently purges the account + data and <strong>frees the email for re-registration</strong>.</p>
                    <div className="flex flex-col gap-1.5">
                      <button data-testid="action-delete-confirm" onClick={() => executeAction(actionModal.user.user_id, 'delete')}
                        className="w-full py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#DC2626' }}>Soft delete (keep record)</button>
                      <button data-testid="action-hard-delete-confirm" onClick={() => executeAction(actionModal.user.user_id, 'hard_delete')}
                        className="w-full py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: '#7F1D1D' }}>Hard delete — free email for re-registration</button>
                      <button onClick={() => setConfirmDelete(false)} className="w-full py-1.5 rounded text-xs font-medium" style={{ backgroundColor: '#F9F9F9', color: '#1A1209' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="action-delete" onClick={() => setConfirmDelete(true)}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2"
                    style={{ backgroundColor: '#FEF2F2', color: '#7F1D1D' }}>
                    <Trash2 size={14} /> Delete account…
                  </button>
                )}
              </div>
            </div>

            <button onClick={() => { setActionModal(null); setActionResult(null); setConfirmDelete(false); setConfirmQuarantine(false); }}
              data-testid="action-modal-close"
              className="mt-4 w-full py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: '#F9F9F9' }}>Close</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
